import { useCallback } from 'react'

import { SupportedChainId } from '~/constants/chains'
import { QUERY_KEYS } from '~/constants/react-query'
import { useQueryWrapper } from '~/hooks/useQueryWrapper'
import { useServerRequests } from '~/hooks/useRequests'
import { TokenFeatures, TokenFeaturesResponse } from '~/types'
import { Address } from '~/utils/address'

export const useTokenFeatures = () => {
  const { serverGetRequest } = useServerRequests()

  const fetchTokenFeatures = useCallback(async () => {
    const tokenFeaturesResponse = await serverGetRequest<TokenFeaturesResponse>(
      {
        path: '/api/tokens/features',
      },
    )
    return tokenFeaturesResponse?.data.tokens
  }, [serverGetRequest])

  const {
    data: tokenFeatures,
    isLoading,
    isSuccess,
  } = useQueryWrapper<TokenFeaturesResponse['tokens']>({
    queryKey: [QUERY_KEYS.TOKEN_FEATURES],
    queryFn: fetchTokenFeatures,
  })

  /**
   * Checks if the given token has the requested feature.
   *
   * If the response is undefined, there is not enough information to determine feature support (yet).
   */
  const hasFeature = useCallback(
    (
      chainId: SupportedChainId,
      address: Address.Type,
      feature: keyof TokenFeatures,
    ): boolean | undefined => {
      if (!tokenFeatures) return undefined

      const token = tokenFeatures?.find(
        (t) => t.chainId === chainId && Address.equal(t.address, address),
      )
      if (!token) return false

      return token.features[feature]
    },
    [tokenFeatures],
  )

  return {
    hasFeature,
    tokenFeatures,
    isLoading,
    isSuccess,
  }
}
